import { FC, useState } from 'react';
import cn from 'classnames';
import styles from './OrganizationImageBlockAddPage.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageBlockResponseDto } from '../../../store/buyerAPI/block/dto/response/image-block-response.dto';
import { ImageSize } from '../../../store/buyerAPI/block/dto/image-size';
import { BlockType } from '../../../store/buyerAPI/block/dto/block-type';
import { getUUID } from '../../../utils/UUID';
import { ImageMeta } from '../../../store/mediaAPI/media/dto/request/media-save-request';
import { MediaSaveResponseDto } from '../../../store/mediaAPI/media/dto/response/media-save-response';
import { saveMedia } from '../../../store/mediaAPI/media/reducers/media.slice';
import { ImageBlockSaveRequestDto } from '../../../store/buyerAPI/block/dto/request/image-block-save-request.dto';
import { EntityType } from '../../../store/buyerAPI/block/entity-type';
import {
  fetchBlocks,
  saveImageBlock,
} from '../../../store/buyerAPI/block/reducers/block.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import { OrganizationImageBlockAddTemplate } from '../../templates';

interface OrganizationImageBlockAddPageProps {}

const OrganizationImageBlockAddPage: FC<
  OrganizationImageBlockAddPageProps
> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [imageBlock, setImageBlock] = useState<ImageBlockResponseDto>({
    imageUrl: '',
    backgroundImageUrl: '',
    backgroundColor: '',
    imageAltText: '',
    withTexture: false,
    header: false,
    imageSize: ImageSize.Default,
    blockType: BlockType.ImageBlock,
    organizationId: organizationId ?? '',
    id: getUUID(),
    versionId: 0,
    createdByUserId: '',
    createdOnDateTimeUtc: '',
    language: '',
    published: true,
  });

  const [imageMeta, setImageMeta] = useState<ImageMeta>();
  const [backgroundImageMeta, setBackgroundImageMeta] = useState<ImageMeta>();
  const { imageBlockStatus: status } = useSelector(
    (state: RootState) => state.organizationBlockSlice
  );
  const { status: mediaStatus } = useSelector(
    (state: RootState) => state.mediaSlice
  );

  const handleOnChange = (imageBlock: ImageBlockResponseDto) => {
    setImageBlock(imageBlock);
  };

  const handleImageOnChange = (imageMeta: ImageMeta) => {
    setImageMeta(imageMeta);
  };

  const handleBackgroundOnChange = (imageMeta: ImageMeta) => {
    setBackgroundImageMeta(imageMeta);
  };

  const saveImage = async (
    imageUrl: string | undefined,
    imageMeta: ImageMeta | undefined
  ): Promise<MediaSaveResponseDto> => {
    if (imageUrl && imageUrl !== '' && imageMeta) {
      const formData = new FormData();
      formData.append(
        'file',
        new Blob([imageUrl], { type: imageMeta.imageType ?? 'image/png' }),
        imageMeta.imageSrc
      );
      return (await dispatch(saveMedia(formData)))
        .payload as MediaSaveResponseDto;
    }
    return { href: '' };
  };

  const callbackFn = async (imageBlock: ImageBlockResponseDto) => {
    const image = await saveImage(imageBlock.imageUrl ?? '', imageMeta);
    const backgroundImage = await saveImage(
      imageBlock.backgroundImageUrl ?? '',
      backgroundImageMeta
    );
    const imageBlockToSave: ImageBlockSaveRequestDto = {
      imageUrl: image.href,
      backgroundImageUrl: backgroundImage.href,
      backgroundColor: imageBlock.backgroundColor,
      imageAltText: imageBlock.imageAltText,
      withTexture: imageBlock.withTexture,
      header: imageBlock.header,
      imageSize: imageBlock.imageSize,
      blockType: imageBlock.blockType,
      organizationId: imageBlock.organizationId,
      id: imageBlock.id,
      published: imageBlock.published,
      entityType: EntityType.Buyer,
      entityId: organizationId ?? '',
    };
    await dispatch(saveImageBlock(imageBlockToSave));
    dispatch(fetchBlocks(organizationId!));
    navigate(`/organizations/edit/${organizationId}/content-blocks`);
  };

  if (status === 'loading' || mediaStatus === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      <OrganizationImageBlockAddTemplate
        handleOnChange={handleOnChange}
        handleImageOnChange={handleImageOnChange}
        handleBackgroundOnChange={handleBackgroundOnChange}
        imageBlock={imageBlock}
        callbackFn={callbackFn}
      />
    </div>
  );
};

export default OrganizationImageBlockAddPage;
